import { merge } from 'lodash-es';
import PerfectScrollbar from 'perfect-scrollbar';

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';

import { Subject, fromEvent } from 'rxjs';
import { debounceTime, filter, take, takeUntil } from 'rxjs/operators';

import {
  CustomDialogWithActionsComponent,
  DialogColors,
  DialogConfig,
  DialogConfigTyped,
  DialogTypes,
} from './config';
import { DialogBehaviorService, DialogDataService } from './services';

@Component({
  selector: 'mw-dialog',
  templateUrl: 'dialog.component.html',
  styleUrls: ['./dialog.component.scss'],
  providers: [DialogDataService, DialogBehaviorService],
})
export class MwDialogComponent<T> implements AfterViewInit, OnDestroy {
  @ViewChild('customContent', { read: ViewContainerRef })
  customContent: ViewContainerRef;
  @ViewChild('customText', { read: ViewContainerRef })
  customText: ViewContainerRef;
  @ViewChild('contentWrapper') contentWrapper: ElementRef;

  get disableScroll(): boolean {
    return this._config.disableScroll === true;
  }

  get isCancelDisabled(): boolean {
    return this.dataService.getDisabledStateInstant('cancelButton');
  }

  set isCancelDisabled(value: boolean) {
    this.dataService.disable(value, 'cancelButton');
  }

  get isConfirmDisabled(): boolean {
    return this.dataService.getDisabledStateInstant('confirmButton');
  }

  set isConfirmDisabled(value: boolean) {
    this.dataService.disable(value, 'confirmButton');
  }

  additionalActionsTemplateRef: TemplateRef<any>;
  customContentComponentRef: T;
  dialogColors = DialogColors;
  showAdditionalActions: boolean;
  showTertiaryActions: boolean;
  scrollable = false;
  isConfirmEnabled = true;
  isCancelEnabled = true;
  isAdditionalButtonEnabled = true;
  isConfirmInProgress = false;
  isAdditionalButtonInProgress = false;

  @HostBinding('class.mw-dialog--fullscreen')
  isFullScreen = false;

  readonly config: DialogConfig;
  readonly onConfirmClicked =
    this.behaviorService.onConfirmClicked.asObservable();
  readonly onAdditionalButtonClicked =
    this.behaviorService.additionalButtonClick.asObservable();
  readonly onConfirmEnabledChange =
    this.behaviorService.onConfirmEnabledChange.asObservable();
  readonly onCancelEnabledChange =
    this.behaviorService.onCancelEnabledChange.asObservable();
  readonly onAdditionalButtonEnabledChange =
    this.behaviorService.onAdditionalButtonEnabledChange.asObservable();
  readonly onAdditionalButtonVisibilityChange =
    this.behaviorService.onAdditionalButtonVisibilityChange.asObservable();
  readonly onAdditionalButtonInProgress =
    this.behaviorService.onAdditionalButtonInProgress.asObservable();
  readonly onConfirmInProgress =
    this.behaviorService.onConfirmInProgress.asObservable();
  readonly onCloseDialog = this.behaviorService.closeDialog.asObservable();
  readonly dialogResult$ = this.behaviorService.dialogResult.asObservable();
  readonly onAdditionalActionsTemplateChange =
    this.behaviorService.onAdditionalActionsTemplateChange.asObservable();
  readonly onTertiaryButtonEnabledChange =
    this.behaviorService.onTertiaryButtonEnabledChange.asObservable();
  readonly onTertiaryButtonVisibilityChange =
    this.behaviorService.onTertiaryButtonVisibilityChange.asObservable();
  readonly onTertiaryButtonInProgress =
    this.behaviorService.onTertiaryButtonInProgress.asObservable();

  private perfectScrollbar: PerfectScrollbar;
  private readonly unsubscribeAll$ = new Subject<void>();
  private readonly initialShowAdditionalButton: boolean;
  private readonly _defaultConfig: DialogConfig = {
    dialogType: 'mw-dialog-medium',
    colors: DialogColors.Accent,
    autoClose: true,
    translations: {
      title: '',
      text: '',
      yesButton: 'GENERAL.BUTTON.ok',
      noButton: 'GENERAL.BUTTON.cancel',
    },
    showFooter: true,
    showHeader: true,
  };

  constructor(
    private readonly dataService: DialogDataService,
    private readonly behaviorService: DialogBehaviorService,
    @Inject(MAT_DIALOG_DATA) private readonly _config: DialogConfigTyped<T>,
    private readonly dialogRef: MatDialogRef<MwDialogComponent<T>>,
    private readonly changeDetectionRef: ChangeDetectorRef
  ) {
    this.config = merge({}, this._defaultConfig, this._config);
    this.dataService.setConfig(this.config);
    this.additionalActionsTemplateRef =
      this.config.additionalActionsTemplateRef;
    this.isFullScreen = this.config.dialogType === 'mw-dialog-full-screen';
    this.initialShowAdditionalButton =
      !!this.config.translations.additionalButton &&
      this.config.dialogType !== DialogTypes.XSmall;

    this.showAdditionalActions = this._config.showAdditionalButton;
    this.showTertiaryActions = this._config.showTertiaryButton;

    this.subscribeToBehaviorService();
  }

  ngOnDestroy(): void {
    if (this.perfectScrollbar) {
      this.perfectScrollbar.destroy();
      this.perfectScrollbar = null;
    }
    this.unsubscribeAll$.next();
    this.unsubscribeAll$.complete();
  }

  ngAfterViewInit(): void {
    if (this.config?.customContentComponent) {
      this.setupCustomComponent();
    }
    this.changeDetectionRef.detectChanges();

    if (!this._config?.disableScroll) {
      this.subscribeToWindowsResize();
      this.setupContentScrollState();

      this.changeDetectionRef.detectChanges();

      this.perfectScrollbar = new PerfectScrollbar(
        this.contentWrapper.nativeElement
      );
    }
  }

  close(result: any): void {
    this.handleDialogClose(result);
  }

  private setupContentScrollState(): void {
    this.scrollable =
      this.customText?.element.nativeElement.offsetHeight >
      this.contentWrapper.nativeElement.offsetHeight;
  }

  private subscribeToWindowsResize(): void {
    fromEvent(window, 'resize')
      .pipe(debounceTime(300), takeUntil(this.unsubscribeAll$))
      .subscribe(() => {
        this.setupContentScrollState();
      });
  }

  resetProgressState(): void {
    this.behaviorService.onConfirmInProgress.next(false);
    this.behaviorService.onAdditionalButtonInProgress.next(false);
  }

  private setupCustomComponent(): void {
    const ref = this.customContent.createComponent(
      this.config?.customContentComponent,
      {
        injector: this.config?.injector,
        ngModuleRef: this.config?.ngModuleRef,
      }
    );
    this.customContentComponentRef = ref.instance;

    if (
      this.customContentComponentRef instanceof CustomDialogWithActionsComponent
    ) {
      this.behaviorService.setupCustomComponentActions(
        this.customContentComponentRef
      );
      this.customContentComponentRef.data = this.config.data;
    }
  }

  private subscribeToBehaviorService(): void {
    this.behaviorService.closeDialog.subscribe((result) =>
      this.handleDialogClose(result)
    );

    this.behaviorService.onAdditionalActionsTemplateChange
      .pipe(filter((template) => !!template))
      .subscribe(
        (templateRef) => (this.additionalActionsTemplateRef = templateRef)
      );

    if (!this.config.disableClose) {
      this.behaviorService.onCancelEnabledChange.subscribe((enable) => {
        this.dialogRef.disableClose = !enable;
      });
    }

    if (this.initialShowAdditionalButton) {
      this.behaviorService.onAdditionalButtonVisibilityChange.subscribe(
        (show) => {
          this.showAdditionalActions = show;
          this.changeDetectionRef.detectChanges();
        }
      );
    } else {
      this.showAdditionalActions = false;
    }

    this.behaviorService.onTertiaryButtonVisibilityChange.subscribe((show) => {
      this.showTertiaryActions = show;
      this.changeDetectionRef.detectChanges();
    });
  }

  private handleDialogClose(dialogResult: any): void {
    if (this.config.beforeClose) {
      this.config
        .beforeClose(dialogResult)
        .pipe(
          takeUntil(this.unsubscribeAll$),
          filter((result) => result),
          take(1)
        )
        .subscribe(() => {
          this.dialogRef.close(dialogResult);
        });
    } else {
      this.dialogRef.close(dialogResult);
    }
  }
}
